// Self-Learning Pattern Engine — zero AI, pure statistics over JARVIS's own
// real trade history. Slices real closed trades by real dimensions
// (timeframe, direction, session, day of week, ADX bucket, HTF alignment)
// and flags where a bucket's win rate differs meaningfully from the
// overall baseline.
//
// Only runs on trades that carry real signal context — i.e. JARVIS's own
// auto-logged trades (scriptAnalysis.js's autoLogSignalAsTrade), which now
// store adx_at_signal/trend_at_signal/htf_aligned/timeframe. Manually
// entered trades have those fields as null and are excluded — there's no
// signal to attribute a pattern to for those.
//
// Discipline (same "never fabricate confidence" rule the rest of this app
// is built on): a finding is NEVER shown unless it clears BOTH a minimum
// sample size AND a minimum gap from baseline. Every finding always
// carries its raw win/total counts alongside the percentage — never just
// a bare claim. Nothing here ever blocks or auto-adjusts a trade; the
// engine only ever produces suggestions for Sir to review and decide on.

import { getTrades } from '../services/storage';

export const MIN_SAMPLE_SIZE = 15;    // per bucket, before ANY comparison is shown
export const SIGNIFICANT_GAP_PP = 15; // percentage points away from baseline to flag

function closedWithContext(trades) {
  return trades.filter((t) =>
    t.result && t.result !== 'OPEN' && t.adx_at_signal != null
  );
}

function winRate(group) {
  if (!group.length) return null;
  const wins = group.filter((t) => t.result === 'WIN').length;
  return { rate: Math.round((wins / group.length) * 100), wins, total: group.length };
}

function bucketBy(trades, keyFn) {
  const groups = {};
  for (const t of trades) {
    const k = keyFn(t);
    if (k == null) continue;
    (groups[k] = groups[k] || []).push(t);
  }
  return groups;
}

function adxBucket(adx) {
  if (adx == null) return null;
  if (adx < 25) return '20–25';
  if (adx < 30) return '25–30';
  return '30+';
}

// Compares each bucket's win rate to the OVERALL baseline (all
// context-carrying closed trades), flags only if sample size AND gap both
// clear the bar. Sorted so the largest, most sample-backed gaps lead.
function findGaps(trades, dimension, keyFn, labelFn) {
  const baseline = winRate(trades);
  if (!baseline) return [];
  const groups = bucketBy(trades, keyFn);
  const findings = [];
  for (const [key, group] of Object.entries(groups)) {
    if (group.length < MIN_SAMPLE_SIZE) continue; // not enough data — say nothing, not a guess
    const wr = winRate(group);
    const gap = wr.rate - baseline.rate;
    if (Math.abs(gap) >= SIGNIFICANT_GAP_PP) {
      findings.push({
        dimension,
        label: labelFn(key),
        winRate: wr.rate,
        wins: wr.wins,
        total: wr.total,
        baselineRate: baseline.rate,
        gap,
        better: gap > 0,
      });
    }
  }
  return findings;
}

export function computePatterns() {
  const all = getTrades();
  const trades = closedWithContext(all);

  if (trades.length < MIN_SAMPLE_SIZE) {
    return {
      ready: false,
      contextTrades: trades.length,
      needed: MIN_SAMPLE_SIZE,
      findings: [],
    };
  }

  const baseline = winRate(trades);
  const findings = [
    ...findGaps(trades, 'Timeframe', (t) => t.timeframe, (k) => `${k} timeframe`),
    ...findGaps(trades, 'Direction', (t) => t.direction, (k) => `${k} trades`),
    ...findGaps(trades, 'Session', (t) => t.session, (k) => `${k} session`),
    ...findGaps(trades, 'Day', (t) => t.day_of_week, (k) => `${k}`),
    ...findGaps(trades, 'ADX', (t) => adxBucket(t.adx_at_signal), (k) => `ADX ${k}`),
    ...findGaps(
      trades, 'HTF',
      (t) => (t.htf_aligned === true ? 'aligned' : t.htf_aligned === false ? 'not-aligned' : null),
      (k) => (k === 'aligned' ? 'HTF aligned' : 'HTF not aligned')
    ),
  ].sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));

  return {
    ready: true,
    contextTrades: trades.length,
    baselineWinRate: baseline.rate,
    findings,
    computedAt: Date.now(),
  };
}
